/**
 * Typography for markdown-rendered guide content, wherever `.guide-article`
 * appears -- the Guides tab's own reader (`pages/Guides.tsx`) AND the
 * embedded policy preview inside the portal's policy-acceptance screens
 * (`pages/StaffPortal.tsx`'s `PolicyGateScreen`). Both render the SAME
 * staged `docs/guides/policy.md` HTML through the SAME `.guide-article`
 * class; before this file existed the CSS lived only inside Guides.tsx's
 * own `<style>` tag, so the policy text embedded in the gate screen (which
 * never mounts GuidesPage) rendered with none of it -- bordered tables and
 * numbered headings on the Guides tab, and the same content completely
 * unstyled the first time a new person actually has to read it. One export,
 * rendered by both, so that gap cannot reopen by one of the two drifting.
 *
 * Round 15, Unit 13(a): a real typography pass, tested against
 * docs/guides/policy.md as the worst case on this platform -- a long legal
 * document with wide multi-column tables, deeply nested numbered headings
 * (## 1, ### 5.1, ### 5.3) and one lead paragraph the docs teammate wrote in
 * bold as its own callout. Every selector is scoped under .guide-article,
 * never a bare tag name, so this never touches the arcade's own scoreboard
 * chrome -- the one exception is h1, `!important`-overridden below, because
 * `.arcade h1` (arcadeCss.ts) already claims that bare tag globally for the
 * retro display font/accent-colour/letter-spacing treatment a scoreboard
 * header wants and a policy document's own title does not.
 */
export const GUIDE_ARTICLE_CSS = `
  .guide-article img { max-width: 100%; height: auto; display: block; margin: 1rem 0; border: 1px solid var(--a-line); border-radius: 8px; }
  .guide-article pre { max-width: 100%; overflow-x: auto; }
  .guide-article [role="img"] { max-width: 100%; }

  /* Prose measure: ~75 characters, the width every readability guide lands
     on, so a paragraph does not run edge to edge of a wide column. Tables
     and headings are exempt below -- a table needs its own columns' width,
     not prose width, and a heading reads fine wider than its body text. */
  .guide-article p, .guide-article ul, .guide-article ol {
    max-width: 75ch;
    margin: 0 0 1rem;
  }
  .guide-article li { margin-bottom: 0.4rem; }
  .guide-article li:last-child { margin-bottom: 0; }
  .guide-article ul, .guide-article ol { padding-left: 1.4rem; }
  /* A list nested inside another gets its own breathing room without
     doubling the OUTER list's own margin-bottom. */
  .guide-article li > ul, .guide-article li > ol { margin: 0.4rem 0 0; }

  /* Headings. Numbered section headings (## 1. Purpose, ### 5.1 The ELT...)
     get real top spacing so a reader can tell a new section started without
     counting numbers -- the original defect this was built for. */
  .guide-article h1 {
    font-family: var(--a-fb-font) !important;
    font-size: 1.6rem !important;
    font-weight: 700 !important;
    color: var(--a-ink) !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    text-shadow: none !important;
    margin: 0 0 1rem !important;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid var(--a-line);
  }
  .guide-article h1:not(:first-child) { margin-top: 2.2rem !important; }
  .guide-article h2 {
    font-family: var(--a-fb-font);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--a-ink);
    margin: 2rem 0 0.8rem;
  }
  .guide-article h2:first-child { margin-top: 0; }
  .guide-article h3 {
    font-family: var(--a-fb-font);
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--a-ink);
    margin: 1.5rem 0 0.6rem;
  }

  /* A horizontal rule (the docs teammate uses --- as a section break
     between Part A's cover note and Part B's own numbered sections) reads
     as a thin divider, not a heavy default browser groove. */
  .guide-article hr { border: none; border-top: 1px solid var(--a-line); margin: 1.6rem 0; }

  /* Tables: bordered, zebra-striped, a shaded header row, and real cell
     padding -- the roles table (docs/guides/policy.md section 5.3, three
     columns of full sentences) is the worst case this was built against:
     without wrapping, its longest cell ran off the printed page width
     entirely. */
  .guide-article table {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    display: block;
    border-collapse: collapse;
    margin: 0 0 1.2rem;
    font-size: 0.92rem;
  }
  .guide-article table thead { background: var(--a-panel2); }
  .guide-article table th {
    text-align: left;
    padding: 0.6rem 0.9rem;
    border: 1px solid var(--a-line);
    font-weight: 700;
    color: var(--a-ink);
    white-space: normal;
  }
  .guide-article table td {
    padding: 0.6rem 0.9rem;
    border: 1px solid var(--a-line);
    color: var(--a-ink);
    vertical-align: top;
    /* The actual fix for the roles table: a long sentence in a cell WRAPS
       at the column's own width instead of forcing the table (and the
       whole page) wider than the viewport. */
    white-space: normal;
    overflow-wrap: break-word;
  }
  .guide-article table tbody tr:nth-child(even) { background: var(--a-panel2); }

  /* Blockquotes, styled as a callout box -- not currently used by markdown
     >-syntax in any live guide, but a real rule rather than browser default
     indent-and-italic for whenever one is. */
  .guide-article blockquote {
    margin: 0 0 1rem;
    padding: 0.8rem 1rem;
    background: var(--a-panel2);
    border-left: 4px solid var(--a-accent);
    border-radius: 6px;
    color: var(--a-ink);
  }
  .guide-article blockquote p:last-child { margin-bottom: 0; }

  /* The docs teammate's OWN callout, written as a lead paragraph in bold
     rather than blockquote syntax (docs/guides/policy.md's "Version and
     status." paragraph) -- matched structurally, by being the one
     paragraph whose first child is a bold run, rather than by any special
     markdown syntax the source would need to carry. Checked live: exactly
     one paragraph in policy.md matches this today. */
  .guide-article p:has(> strong:first-child) {
    padding: 0.8rem 1rem;
    background: var(--a-panel2);
    border-left: 4px solid var(--a-accent);
    border-radius: 6px;
  }

  .guide-article code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
`
